import React from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowRightIcon,
  DownloadIcon,
  EyeIcon,
  FileTextIcon,
  RefreshCwIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react'
import { Accept, useDropzone } from 'react-dropzone'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'

import { formatBytes } from '@/lib/utils'

import { ResumeAlphaData } from '../hooks/use-resume-tailor'
import { Button } from '../ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import { ScrollArea } from '../ui/scroll-area'
import { Textarea } from '../ui/textarea'

export const UploadStage = (props: {
  selectedFiles: FileData[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileData[]>>
  data: ResumeAlphaData
  setData: React.Dispatch<React.SetStateAction<ResumeAlphaData>>
  isMovingToNextStage: boolean
  onNextStep: () => void
}) => {
  const { selectedFiles, setSelectedFiles, data, setData } = props

  return (
    <div>
      <div className="grid gap-6 lg:grid-cols-2">
        <BaseResumeUploader
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          onUploadChange={(files) => {
            if (files?.[0]) {
              const text = files?.[0].text
              setData((prev) => ({
                ...prev,
                input: { ...prev.input, resume: text },
              }))
            }
          }}
        />
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <FileTextIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <CardTitle>Job Description</CardTitle>
                <CardDescription>
                  Paste the job posting you're applying to
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={data?.input?.job_description}
              onChange={(e) =>
                setData((prev) => ({
                  ...prev,
                  input: {
                    ...prev.input,
                    job_description: e.target.value,
                  },
                }))
              }
              placeholder="Paste the full job description here..."
              className="max-h-50 min-h-50 resize-none"
            />
          </CardContent>
        </Card>
      </div>
      <div className="mt-8 flex justify-center">
        <Button
          onClick={() => {
            props.onNextStep && props.onNextStep()
          }}
          size="lg"
          className="gap-2"
          disabled={
            !data.input.resume ||
            !data.input.job_description ||
            props.isMovingToNextStage
          }
        >
          {props.isMovingToNextStage ? (
            <>
              <RefreshCwIcon className="h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              Analyze Resume
              <ArrowRightIcon className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

const ACCEPTED_FORMATS = { 'text/markdown': ['.md'] }

type FileData = { name: string; type: string; size: number; text: string }

export const useResumeUploader = (props: {
  onDrop: (acceptedFiles: File[]) => void
  accept: Accept
}) => {
  const dropzone = useDropzone({
    onDrop: props.onDrop,
    maxSize: MAX_FILE_SIZE,
    maxFiles: 1,
    accept: props.accept,
  })

  return { ...dropzone, MAX_FILE_SIZE }
}

function BaseResumeUploader(props: {
  selectedFiles: FileData[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileData[]>>
  onUploadChange?: (files: FileData[]) => void
}) {
  const { selectedFiles, setSelectedFiles } = props

  React.useEffect(() => {
    props.onUploadChange && props.onUploadChange(selectedFiles)
  }, [selectedFiles])

  const onDrop = React.useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return

    const processedFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const text = await file.text()

        return {
          name: file.name,
          type: file.type,
          size: file.size,
          text: text,
        }
      }),
    )

    setSelectedFiles((prev) => [...prev, ...processedFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useResumeUploader({
    accept: ACCEPTED_FORMATS,
    onDrop,
  })

  const removeFile = React.useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleDownload = React.useCallback((file: FileData) => {
    if (!file) return

    const blob = new Blob([file.text], {
      type: file.type,
    })

    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = file.name

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setTimeout(() => URL.revokeObjectURL(url), 100)
  }, [])

  const remarkPlugins = React.useMemo(
    () => [remarkGfm, remarkFrontmatter, remarkBreaks],
    [],
  )

  const rehypePlugins = React.useMemo(() => [rehypeRaw], [])

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <UploadIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <CardTitle>Base Resume</CardTitle>
            <CardDescription>
              Upload your base resume in markdown format. See
              <Button
                variant="link"
                className="px-1"
                nativeButton={false}
                render={(props) => (
                  <Link {...props} to={'/templates'}>
                    templates
                  </Link>
                )}
              />
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex w-full min-w-0 flex-col space-y-4">
          {selectedFiles.length === 0 && (
            <div
              {...getRootProps()}
              className="cursor-pointer rounded-lg border-2 border-dashed px-4 py-2 text-center transition-colors hover:bg-muted/50"
            >
              <input {...getInputProps()} />
              <UploadIcon className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">
                {isDragActive
                  ? 'Drop the resume here...'
                  : 'Click to upload or drag and drop'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {Object.values(ACCEPTED_FORMATS).join(', ')} only.{' '}
                {`(Max Size: ${formatBytes(MAX_FILE_SIZE)})`}
              </p>
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div className="space-y-2 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center gap-1 rounded-md bg-muted/50 px-4 py-2"
                >
                  <div className="absolute top-1 right-1 flex flex-row items-center gap-1">
                    <Dialog>
                      <DialogTrigger
                        render={(props) => (
                          <Button
                            {...props}
                            size="icon"
                            variant="outline"
                            className="size-8 cursor-pointer"
                          >
                            <EyeIcon />
                          </Button>
                        )}
                      />
                      <DialogContent className="max-w-auto flex max-h-[80vh] min-h-0 w-fit min-w-fit flex-col">
                        <DialogHeader>
                          <DialogTitle>Resume</DialogTitle>
                        </DialogHeader>
                        <div className="flex h-full min-h-0 flex-col">
                          <ScrollArea className="flex h-full min-h-0 w-full flex-col bg-muted-foreground">
                            <div className="prose w-274 min-w-274 origin-top scale-75 bg-white p-24">
                              <Markdown
                                rehypePlugins={rehypePlugins}
                                remarkPlugins={remarkPlugins}
                              >
                                {selectedFiles?.[0]?.text}
                              </Markdown>
                            </div>
                          </ScrollArea>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button
                      size="icon"
                      className="size-8 cursor-pointer"
                      onClick={() =>
                        selectedFiles?.[0] && handleDownload(selectedFiles?.[0])
                      }
                    >
                      <DownloadIcon className="size-3" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="size-8 shrink-0 cursor-pointer"
                      onClick={() => removeFile(index)}
                    >
                      <XIcon className="size-3" />
                    </Button>
                  </div>
                  <FileTextIcon className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{file.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
